import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  Form,
  FormGroup,
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import ExpenseService from '../../../Services/expenses/ExpenseService';

/**
 * @param {boolean} isOpen
 * @param {() => void} toggle
 * @param {'contributor' | 'inhouse'} variant
 */
function AddExpenseModal({ isOpen, toggle, variant = 'contributor' }) {
  const queryClient = useQueryClient();
  const fileRef = useRef(null);

  const { data: catRes, isPending: catLoading } = useQuery({
    queryKey: ['expenseCategories', variant],
    queryFn: () =>
      variant === 'contributor'
        ? ExpenseService.getContributorCategories()
        : ExpenseService.getInhouseCategories(),
    enabled: true,
    staleTime: 60 * 1000,
  });

  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [curLoadingState, setCurLoadingState] = useState(false);
  const [curError, setCurError] = useState(false);
  const [curErrorDetail, setCurErrorDetail] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    setCurLoadingState(true);
    setCurError(false);
    setCurErrorDetail(null);

    ExpenseService.getCurrencies()
      .then((res) => {
        const opts = res?.options;
        if (Array.isArray(opts)) {
          setCurrencyOptions(opts);
          return;
        }

        const data = res?.data;
        if (Array.isArray(data) && data.length > 0) {
          const normalized = data
            .map((row) => {
              if (!row || typeof row !== 'object') return null;
              const code = String(row.code ?? row.CODE ?? '').toUpperCase().slice(0, 3);
              const name = String(row.name ?? row.NAME ?? code);
              return code.length === 3 ? { code, name } : null;
            })
            .filter(Boolean);
          setCurrencyOptions(normalized);
          return;
        }

        setCurrencyOptions([]);
      })
      .catch((err) => {
        setCurError(true);
        setCurErrorDetail(err);
        setCurrencyOptions([]);
      })
      .finally(() => {
        setCurLoadingState(false);
      });
  }, [isOpen]);

  const { data: artistsPayload, isPending: artLoading } = useQuery({
    queryKey: ['expenseArtists'],
    queryFn: ExpenseService.getArtists,
    enabled: true,
    staleTime: 60 * 1000,
  });

  const categories = catRes?.data ?? [];
  const artists = artistsPayload?.artists ?? [];

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      category_id: '',
      artist_id: '',
      currencies: '',
      status: '',
      amount: '',
    },
  });

  const artistId = watch('artist_id');
  const selectedCurrency = watch('currencies');

  useEffect(() => {
    if (!artistId || !artists.length) return;
    const artist = artists.find((a) => String(a.id) === String(artistId));
    if (artist?.currency) {
      const code = String(artist.currency).toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
      if (code.length === 3) {
        setValue('currencies', code);
      }
    }
  }, [artistId, artists, setValue]);

  // If currencies loaded but no selection yet, default to the first currency.
  // This avoids the user seeing an empty/placeholder-only select.
  useEffect(() => {
    if (!isOpen) return;
    if (Array.isArray(currencyOptions) && currencyOptions.length > 0) {
      if (!selectedCurrency) {
        setValue('currencies', currencyOptions[0].code);
      }
    }
  }, [isOpen, currencyOptions, selectedCurrency, setValue]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (isOpen && curError) {
      toast.error(
        curErrorDetail?.message ||
          'Could not load currencies. Check API /api/currencies and network.',
        { toastId: 'expense-currencies-error' }
      );
    }
  }, [isOpen, curError, curErrorDetail]);

  const mutation = useMutation({
    mutationFn: (formData) => ExpenseService.createExpense(formData),
    onSuccess: (res) => {
      if (res?.success) {
        toast.success('Expense saved');
        queryClient.invalidateQueries({ queryKey: ['inhouseExpenses'] });
        queryClient.invalidateQueries({ queryKey: ['inhouseCategories'] });
        toggle();
        reset();
        if (fileRef.current) fileRef.current.value = '';
      }
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.category?.[0] ||
        'Failed to save expense';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save expense');
    },
  });

  const onSubmit = (values) => {
    const artist = artists.find((a) => String(a.id) === String(values.artist_id));
    if (!artist?.label_red_id) {
      toast.error('This artist has no label linked (label_red_id). Choose another artist or fix the artist in the backend.');
      return;
    }

    const fd = new FormData();
    fd.append('artist_id', values.artist_id);
    fd.append('label_id', String(artist.label_red_id));
    fd.append('title', values.title);
    fd.append('description', values.description);
    fd.append('amount', values.amount);
    fd.append('currencies', values.currencies);
    fd.append('status', values.status);

    if (variant === 'contributor') {
      fd.append('category_contributors_id', values.category_id);
    } else {
      fd.append('category_inhouse_id', values.category_id);
    }

    if (fileRef.current?.files?.[0]) {
      fd.append('file', fileRef.current.files[0]);
    }

    mutation.mutate(fd);
  };

  const loading = catLoading || curLoadingState || artLoading;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Add New Expense</ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          {loading && <p className="text-muted">Loading form data…</p>}

          <FormGroup>
            <Label for="exp-title">Title</Label>
            <Controller
              name="title"
              control={control}
              rules={{ required: 'Title is required' }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="exp-title"
                  placeholder="Enter title"
                  invalid={!!errors.title}
                />
              )}
            />
            {errors.title && <div className="text-danger small">{errors.title.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="exp-desc">Description</Label>
            <Controller
              name="description"
              control={control}
              rules={{ required: 'Description is required' }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="exp-desc"
                  type="textarea"
                  placeholder="Enter description"
                  invalid={!!errors.description}
                />
              )}
            />
            {errors.description && (
              <div className="text-danger small">{errors.description.message}</div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="exp-cat">Category</Label>
            <Controller
              name="category_id"
              control={control}
              rules={{ required: 'Category is required' }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="exp-cat"
                  type="select"
                  invalid={!!errors.category_id}
                >
                  <option value="">Select category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </Input>
              )}
            />
            {errors.category_id && (
              <div className="text-danger small">{errors.category_id.message}</div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="exp-artist">Artist</Label>
            <Controller
              name="artist_id"
              control={control}
              rules={{ required: 'Artist is required' }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="exp-artist"
                  type="select"
                  invalid={!!errors.artist_id}
                >
                  <option value="">Select artist</option>
                  {artists.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.artist_name || a.name}
                    </option>
                  ))}
                </Input>
              )}
            />
            {errors.artist_id && (
              <div className="text-danger small">{errors.artist_id.message}</div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="exp-currency">Currency</Label>
            <Controller
              name="currencies"
              control={control}
              rules={{ required: 'Currency is required' }}
              render={({ field }) => (
                <select
                  {...field}
                  id="exp-currency"
                  className={`form-control ${errors.currencies ? 'is-invalid' : ''}`}
                >
                  <option value="">Select currency</option>
                  {currencyOptions.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              )}
            />
            {curError && (
              <div className="text-warning small mt-1">
                Currencies failed to load. Please refresh and try again.
              </div>
            )}
            {!curLoadingState && !curError && currencyOptions.length === 0 && (
              <div className="text-muted small mt-1">
                No currencies in database. Run migration and seed the <code>currencies</code> table (e.g. EUR, TND).
              </div>
            )}
            {errors.currencies && (
              <div className="text-danger small">{errors.currencies.message}</div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="exp-status">Status</Label>
            <Controller
              name="status"
              control={control}
              rules={{ required: 'Status is required' }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="exp-status"
                  type="select"
                  invalid={!!errors.status}
                >
                  <option value="">Select status</option>
                  <option value="Paid">Paid</option>
                  <option value="Unpaid">Unpaid</option>
                  <option value="Draft">Draft</option>
                </Input>
              )}
            />
            {errors.status && <div className="text-danger small">{errors.status.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="exp-amount">Amount</Label>
            <Controller
              name="amount"
              control={control}
              rules={{ required: 'Amount is required' }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="exp-amount"
                  type="number"
                  step="0.01"
                  placeholder="Enter amount"
                  invalid={!!errors.amount}
                />
              )}
            />
            {errors.amount && <div className="text-danger small">{errors.amount.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="expense-file-input">File</Label>
            <Input id="expense-file-input" innerRef={fileRef} type="file" />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" onClick={toggle}>
            Cancel
          </Button>
          <Button color="danger" type="submit" disabled={mutation.isPending || loading}>
            Save
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default AddExpenseModal;
